import { Link } from '@tanstack/react-router'
import { Button } from '@trellis/roots/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@trellis/roots/sheet'
import { Skeleton } from '@trellis/roots/skeleton'
import { ExternalLink } from 'lucide-react'

import { useImportJobQuery } from '@/hooks'

import { ImportJobOverview } from '../ImportJobOverview'
import type { ImportBasePath, ImportPreviewConfig } from '../types'

interface ImportPreviewSheetProps<TRow> extends ImportPreviewConfig<TRow> {
  jobId: string | null
  basePath: ImportBasePath
  onOpenChange: (isOpen: boolean) => void
}

export function ImportPreviewSheet<TRow>({
  jobId,
  basePath,
  previewColumns,
  getPreviewRows,
  onOpenChange,
}: ImportPreviewSheetProps<TRow>) {
  const jobQuery = useImportJobQuery(jobId ?? '')
  const job = jobQuery.data?.data ?? null
  const hasError = jobQuery.isError || (jobQuery.isFetched && !job)

  return (
    <Sheet open={jobId !== null} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(95vw,720px)] max-w-none overflow-y-auto sm:max-w-none"
      >
        <SheetHeader>
          <SheetTitle>{job?.fileName ?? 'Import'}</SheetTitle>
          <SheetDescription>
            Preview of the import before it is committed.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 p-4">
          {jobQuery.isLoading && (
            <div className="space-y-5">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}

          {hasError && (
            <p className="text-destructive-text text-sm">
              Failed to load import details.
            </p>
          )}

          {job && (
            <>
              <ImportJobOverview
                job={job}
                previewColumns={previewColumns}
                getPreviewRows={getPreviewRows}
              />
              <Button variant="outline" asChild>
                <Link to={`${basePath}/$id`} params={{ id: job.id }}>
                  <ExternalLink className="size-4" aria-hidden="true" />
                  View import job
                </Link>
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
